/**
 * The one way V2 code sends anything to Google Tag Manager. See "V2 analytics events" in
 * docs/gtm.md for the rule this implements.
 *
 * Tags that read the visitor's email, hashes, affiliate, state or A/B variant fire on
 * `user_context_loaded`, and so do the GA4 page view and everything that reads those
 * fields on later events. So nothing may reach GTM before that event has: an event pushed
 * first would run its tags against an empty visitor. This module holds every event behind
 * it — queued in the order it was made, flushed the moment the context lands, passed
 * straight through afterwards.
 *
 * Module state rather than component state: a page can have several V2 roots (a legacy
 * page mounts its navigation and footer separately) and they must share one gate.
 */

export const USER_CONTEXT_EVENT = 'user_context_loaded';

export type EmailHashes = {
  md5: string;
  sha1: string;
  sha256: string;
  md5UC: string;
  sha1UC: string;
  sha256UC: string;
};

export type Affiliate = {
  username: string;
  external_id: string;
  external_child_id: string;
};

/**
 * The payload, in exactly the shapes the server-rendered push uses (user_context.html),
 * so GTM variables read it the same way whichever side sent it.
 */
export type UserContext = {
  email: string;
  email_hashes: EmailHashes | null;
  affiliate: Affiliate;
  visitor_state: string;
  /**
   * A string is a variant; null is "known to be in no test", which clears the GA4 user
   * property. Absent means unknown and must stay absent — sending null for "unknown"
   * would wipe the variant of a visitor who is in a test.
   */
  ab_test_variant?: string | null;
};

export const NO_AFFILIATE: Affiliate = { username: '', external_id: '0', external_child_id: '0' };

/** What is pushed when the context does not arrive in time: nothing known, no variant claimed. */
const UNKNOWN_CONTEXT: UserContext = {
  email: '',
  email_hashes: null,
  affiliate: NO_AFFILIATE,
  visitor_state: '',
};

/**
 * Who opened the gate. `page` means the server pushed the context before any V2 code ran
 * — every legacy-rendered page — so nothing here pushes it again. `fallback` means the cart
 * request failed and an empty one was pushed, so a real context arriving later (a cart
 * operation that succeeds afterwards) still has something to correct.
 */
type Gate = 'closed' | 'page' | 'context' | 'fallback';

let gate: Gate = 'closed';
const queue: Array<() => void> = [];

function dataLayer(): unknown[] {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function isUserContextEntry(entry: unknown): boolean {
  return typeof entry === 'object' && entry !== null && (entry as { event?: unknown }).event === USER_CONTEXT_EVENT;
}

function open(by: Exclude<Gate, 'closed'>): void {
  gate = by;
  while (queue.length) {
    queue.shift()!();
  }
}

/** Open, opening it first if the page's own push is already in the dataLayer. */
function isOpen(): boolean {
  if (gate === 'closed' && dataLayer().some(isUserContextEntry)) {
    open('page');
  }
  return gate !== 'closed';
}

/**
 * Pushes `user_context_loaded`, unless the page already has — then releases every event
 * waiting on it.
 *
 * After a failed cart request opened the gate empty, a real context arriving later is
 * pushed once more *without* the event: the tags that fire on it have fired already and
 * must not fire twice, but the values are still worth having for everything afterwards.
 */
export function pushUserContext(context: UserContext): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (isOpen()) {
    if (gate === 'fallback') {
      dataLayer().push({ ...context });
      gate = 'context';
    }

    return;
  }

  dataLayer().push({ event: USER_CONTEXT_EVENT, ...context });
  open('context');
}

/**
 * For a cart request that failed: pushes the event with nothing known about the visitor, so
 * the page's queued events still go out instead of waiting forever. Leaves
 * `ab_test_variant` out on purpose — see UserContext.
 */
export function pushUnknownUserContext(): void {
  if (typeof window === 'undefined' || isOpen()) {
    return;
  }

  dataLayer().push({ event: USER_CONTEXT_EVENT, ...UNKNOWN_CONTEXT });
  open('fallback');
}

/**
 * Sends a GA4 event through the gtag stub the layout writes into the head, once the
 * visitor's context is in the dataLayer. Without the stub GTM is not on the page and the
 * event has nowhere to go.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') {
    return;
  }

  const send = () => window.gtag?.('event', name, params);

  if (isOpen()) {
    send();
  } else {
    queue.push(send);
  }
}
